import {
  DependencyList,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react'

// antd ไม่เปิด ref ของกล่องที่เลื่อนแนวนอน — หาจาก wrapper แทน
// (.ant-table-content = scroll.x อย่างเดียว, .ant-table-body = มี scroll.y ด้วย)
const SCROLLER_SELECTOR = '.ant-table-content, .ant-table-body'

// ตำแหน่งที่จะเลื่อนไป: ขอบซ้ายของคอลัมน์ถัดไป/ก่อนหน้า ไม่เกินขอบตาราง
export const nextColumnScrollLeft = (
  columnStarts: number[],
  current: number,
  maxScrollLeft: number,
  direction: 1 | -1,
): number => {
  const target =
    direction > 0
      ? columnStarts.find((start) => start > current + 1) ?? maxScrollLeft
      : [...columnStarts].reverse().find((start) => start < current - 1) ?? 0
  return Math.min(Math.max(target, 0), maxScrollLeft)
}

const getColumnStarts = (scroller: HTMLElement): number[] => {
  const cells = Array.from(
    scroller.querySelectorAll<HTMLElement>('thead tr:first-child > th'),
  )
  const isFixed = (cell: HTMLElement) =>
    cell.classList.contains('ant-table-cell-fix-left') ||
    cell.classList.contains('ant-table-cell-fix-right')
  // คอลัมน์ที่ตรึงซ้ายบังส่วนต้นของตารางอยู่ — ต้องหักความกว้างออก
  const fixedLeftWidth = cells
    .filter((cell) => cell.classList.contains('ant-table-cell-fix-left'))
    .reduce((width, cell) => width + cell.offsetWidth, 0)
  return cells
    .filter((cell) => !isFixed(cell))
    .map((cell) => cell.offsetLeft - fixedLeftWidth)
}

// เลื่อนตาราง antd ที่กว้างเกินจอ ไปซ้าย/ขวาทีละคอลัมน์ จากปุ่มบนหัวตาราง
// แทนการต้องเลื่อนหน้าลงไปหา scroll bar ท้ายตาราง
export const useTableColumnScroll = (
  wrapperRef: RefObject<HTMLElement>,
  deps: DependencyList = [],
) => {
  const [canPrev, setCanPrev] = useState<boolean>(false)
  const [canNext, setCanNext] = useState<boolean>(false)

  const getScroller = useCallback(
    () =>
      wrapperRef.current?.querySelector<HTMLElement>(SCROLLER_SELECTOR) ?? null,
    [wrapperRef],
  )

  const update = useCallback(() => {
    const scroller = getScroller()
    setCanPrev(!!scroller && scroller.scrollLeft > 1)
    setCanNext(
      !!scroller &&
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1,
    )
  }, [getScroller])

  useEffect(() => {
    const scroller = getScroller()
    update()
    if (!scroller) {
      return
    }
    scroller.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    // ตารางในแท็บที่ยังซ่อนอยู่มีขนาด 0 — ต้องคำนวณใหม่ตอนแท็บโผล่ / ตารางเปลี่ยนความกว้าง
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    resizeObserver?.observe(scroller)
    if (scroller.firstElementChild) {
      resizeObserver?.observe(scroller.firstElementChild)
    }
    return () => {
      scroller.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      resizeObserver?.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getScroller, update, ...deps])

  const scrollByColumn = (direction: 1 | -1) => {
    const scroller = getScroller()
    if (!scroller) {
      return
    }
    scroller.scrollTo({
      left: nextColumnScrollLeft(
        getColumnStarts(scroller),
        scroller.scrollLeft,
        scroller.scrollWidth - scroller.clientWidth,
        direction,
      ),
      behavior: 'smooth',
    })
  }

  return {
    canPrev,
    canNext,
    scrollPrev: () => scrollByColumn(-1),
    scrollNext: () => scrollByColumn(1),
  }
}
